import { mdiArrowTopRight, mdiDeleteOutline, mdiOpenInNew, mdiStarOutline } from '@mdi/js';
import { Divider, PopoverPosition } from '@mui/material';
import React, { Dispatch, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IAppReduxStore } from '../../../../redux/store';
import { updateFavourites } from '../../../../redux/user/sliceUserSettings';
import ContextMenu from '../../../General/ContextMenu/ContextMenu';
import ContextMenuItem from '../../../General/ContextMenu/ContextMenuItem';
import { IEplassProjectData } from '../../../../redux/user/sliceUserProjects';

/**
 * Component that gives project list items a context menu.
 */
export default (props: { config: IProjectContextMenuConfig }) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectSelectionView' });

    /** Allow component to update the application view. */
    const navigate = useNavigate();

    /** List of user's favourited projects. */
    const favourites: string[] = useSelector(
        (store: IAppReduxStore) => store.userSettings.data.favourites.projects,
    );

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Deconstruct props. */
    const { config } = props;

    /** No config, so stop here. */
    if (!config) {
        return <></>;
    }

    const { project, callback } = config;

    const replicaId = project?.replicaId;

    /** Get the position state slice from the config. */
    const [position, setPosition] = config.position;

    /** Check if the project is a user favourite. */
    const fav = !!project && favourites.indexOf(replicaId) >= 0;

    /**
     * Gets the route to the ProjectsView for this project.
     */
    const projectUrl = () => `project/${replicaId}`;

    /**
     * Closes the context menu.
     */
    const closeMenu = () => setPosition(null);

    /**
     * Opens the project.
     */
    const handleOpenClick = () => navigate(`/${projectUrl()}`);

    /**
     * Opens the project in a new Browser Tab.
     */
    const handleOpenNewTabClick = useCallback(() => {
        window.open(`${window.location.href}${projectUrl()}`, '_blank');

        closeMenu();
    }, [project]);

    /**
     * Toggles the project's favourite status.
     */
    const handleFavouriteClick = () => {
        dispatch(updateFavourites({ id: replicaId, favourite: !fav }));

        closeMenu();
    };

    /**
     * Creates a menu item for the context menu.
     * @param text - Context Menu Item Text.
     * @param icon - Icon to shown in the menu item.
     * @param onClick - Function to call when the item is clicked on.
     * @param disabled - (Optional) if true, will disable the menu item.
     */
    const menuItem = (
        text: string,
        icon: string,
        onClick: React.MouseEventHandler,
        disabled?: boolean,
    ) => (
        <ContextMenuItem
            text={t(text)}
            icon={icon}
            onClick={onClick}
            disabled={disabled}
            sx={{ minWidth: 300 }}
        />
    );

    return (
        <ContextMenu
            anchorReference='anchorPosition'
            onClose={closeMenu}
            anchorPosition={position}
            onContextMenu={callback}
            open={!!position}
        >
            {menuItem('open', mdiArrowTopRight, handleOpenClick)}
            {menuItem('openNewTab', mdiOpenInNew, handleOpenNewTabClick)}
            <Divider />
            {menuItem(
                fav ? 'removeFavourite' : 'makeFavourite',
                fav ? mdiDeleteOutline : mdiStarOutline,
                handleFavouriteClick,
            )}
        </ContextMenu>
    );
};

/**
 * Interface that describes the configuration needed for the ProjectContextMenu component.
 */
export interface IProjectContextMenuConfig {
    /** State slice that positions the context menu. */
    position: [PopoverPosition, React.Dispatch<React.SetStateAction<PopoverPosition>>];

    /** Function that allows the context menu to be shown. */
    callback: (event: React.MouseEvent, project?: IEplassProjectData) => void;

    /** The project for which the context menu has been opened. */
    project: IEplassProjectData;
}
